import { FormikProps } from 'formik'

import { TGenericAPIError } from '~/app/models'
import { TKnowledgeTableDataItem } from '~/features/knowledge/v1/models'
import { TContact } from '~/models/features'

export enum ECampaignListItemKeys {
  id = 'id',
  campaignName = 'campaignName',
  status = 'status',
  channel = 'channel',
  startDate = 'startDate',
  createdDate = 'createdDate',
}

export type TCampaignListItem = {
  [ECampaignListItemKeys.id]: string
  [ECampaignListItemKeys.campaignName]: string
  [ECampaignListItemKeys.status]: ECampaignStatus
  [ECampaignListItemKeys.channel]: ECampaignChannel
  [ECampaignListItemKeys.startDate]: string
  [ECampaignListItemKeys.createdDate]: string
}

export enum ECampaignTabs {
  details = 0,
  contacts = 1,
  results = 2,
}

export enum ECampaignExecutionStatus {
  notStarted = 'notStarted',
  inProgress = 'inProgress',
  completed = 'completed',
}

export enum ECampaignKeys {
  agentId = 'agentId',
  campaignFacts = 'campaignFacts',
  campaignName = 'campaignName',
  campaignObjective = 'campaignObjective',
  channel = 'channel',
  createdDate = 'createdDate',
  executionStatus = 'executionStatus',
  id = 'id',
  introMessage = 'introMessage',
  mergeFields = 'mergeFields',
  questions = 'questions',
  rules = 'rules',
  startDate = 'startDate',
  status = 'status',
  updatedDate = 'updatedDate',
  useAgentFacts = 'useAgentFacts',
  voicemailMessage = 'voicemailMessage',
}

export type TCampaign = {
  [ECampaignKeys.agentId]: string
  [ECampaignKeys.campaignFacts]?: TCampaignFact[]
  [ECampaignKeys.campaignName]: string
  [ECampaignKeys.campaignObjective]: string
  [ECampaignKeys.channel]: ECampaignChannel
  [ECampaignKeys.createdDate]: string
  [ECampaignKeys.executionStatus]: ECampaignExecutionStatus
  [ECampaignKeys.id]: string
  [ECampaignKeys.introMessage]?: string
  [ECampaignKeys.mergeFields]: TMergeField[]
  [ECampaignKeys.questions]?: string[]
  [ECampaignKeys.rules]?: string[]
  [ECampaignKeys.startDate]?: string
  [ECampaignKeys.status]: ECampaignStatus
  [ECampaignKeys.updatedDate]: string
  [ECampaignKeys.useAgentFacts]?: boolean
  [ECampaignKeys.voicemailMessage]?: string
}

export type TCampaignCreatePayload = Omit<
  Partial<TCampaign>,
  ECampaignKeys.id | ECampaignKeys.createdDate | ECampaignKeys.updatedDate
>

export type TCampaignError = TGenericAPIError

export type TCampaignUpdatePayload = Omit<
  Partial<TCampaign>,
  ECampaignKeys.agentId | ECampaignKeys.createdDate // we never explicitly update those from the FE.
>

export type TCampaignFact = {
  id?: string
  text: string
}

export type TCampaignFormFields = Omit<
  TCampaign,
  | ECampaignKeys.agentId
  | ECampaignKeys.createdDate
  | ECampaignKeys.updatedDate
  | ECampaignKeys.questions
  | ECampaignKeys.rules
  | ECampaignKeys.campaignFacts
> & {
  [ECampaignKeys.questions]: string
  [ECampaignKeys.rules]: string
  [ECampaignKeys.campaignFacts]: TKnowledgeTableDataItem[]
}

export type TDeleteCampaignContact = {
  campaignContactIds: string[]
}

export type TUpdateCampaignContact = {
  status?: ECampaignContactStatus
  contactMetadata?: Record<string, string>
}

export enum ECampaignContactListItemKeys {
  id = 'id',
  firstName = 'firstName',
  status = 'status',
  createdDate = 'createdDate',
  phone = 'phone',
  customVariables = 'customVariables',
  preview = 'preview',
  testContact = 'testContact',
}

export type TCampaignGetContactListItem = TCampaignGetContact & {
  [ECampaignContactListItemKeys.customVariables]: string
  [ECampaignContactListItemKeys.preview]: string
}

export type TCampaignGetContact = {
  [ECampaignContactListItemKeys.id]: string
  [ECampaignContactListItemKeys.status]: ECampaignContactStatus | null
  campaignId: string | null
  contactId: string
  contactMetadata: Record<string, string> | null
  [ECampaignContactListItemKeys.createdDate]: string | null
  email: string | null
  executionDate: string | null
  executionError: string | null
  [ECampaignContactListItemKeys.firstName]: string | null
  lastName: string | null
  [ECampaignContactListItemKeys.phone]: string | null
  updatedDate: string | null
  contact: TContact | null
  introMessage: string | null
  voicemailMessage: string | null
  testContact: boolean | null
}

export type TCampaignCreateContact = {
  phone?: string
  email?: string
  firstName?: string
  lastName?: string
  status?: ECampaignContactStatus
  testContact?: boolean
  contactMetadata?: Record<string, string>
}

export type TCampaignAnalytics = object

export type TMergeField = {
  id?: string
  friendlyName: string
  required: boolean
  variable: string | null
}

export enum ECampaignChannel {
  phone = 'phone',
  email = 'email',
  text = 'text',
}

export const getFriendlyChannelName = (channel: ECampaignChannel): string => {
  switch (channel) {
    case ECampaignChannel.phone:
      return 'Phone'
    case ECampaignChannel.email:
      return 'Email'
    case ECampaignChannel.text:
      return 'Text'
    default:
      return channel || 'Unspecified'
  }
}

export enum ECampaignStatus {
  draft = 'draft',
  open = 'open',
  closed = 'closed',
}

export const getFriendlyStatusName = (status: ECampaignStatus): string => {
  switch (status) {
    case ECampaignStatus.draft:
      return 'Draft'
    case ECampaignStatus.open:
      return 'Open'
    case ECampaignStatus.closed:
      return 'Closed'
    default:
      return status || 'Unspecified'
  }
}

export enum ECampaignContactStatus {
  new = 'new',
  called = 'called',
  failed = 'failed',
  completed = 'completed',
  started = 'started',
  notStarted = 'notStarted',
}

export type TCampaignOutletContext = {
  formik: FormikProps<TCampaignFormFields>
  showSaveAndTestDialog: boolean
  setShowSaveAndTestDialog: (shouldNavigate: boolean) => void
}

export type TVariableToColumnMap = {
  variable: string
  column: string | undefined
}

export type TAddContactResponse = {
  data: TCampaignGetContact[]
  message: string
  errors: {
    rowIndex: number
    message: string
  }[]
}

export type TAutoParsedColumnTitle = {
  columnTitle: string
  dbFieldConventionalName: string
}

export type TExecuteCampaignResponse = {
  message: string
}

export enum ESaveAndTestForm {
  campaignContactId = 'campaignContactId',
  firstName = 'firstName',
  phone = 'phone',
  mergeFields = 'mergeFields',
}

export type TMergeFieldWithValue = TMergeField & {
  value: string
}

export type TSaveAndTestForm = {
  [ESaveAndTestForm.campaignContactId]: string
  [ESaveAndTestForm.firstName]: string
  [ESaveAndTestForm.phone]: string
  [ESaveAndTestForm.mergeFields]: TMergeFieldWithValue[]
}
